<template>
  <div class="game-container" :class="{ 'night-mode': isNightMode }">
    <div class="header">
      <h1 class="title">晋韵寻踪·古建拼合</h1>
      <div class="mode-toggle" @click="toggleNightMode">
        <div class="toggle-icon" :class="{ 'night': isNightMode }"></div>
        <span>{{ isNightMode ? '日间模式' : '夜间模式' }}</span>
      </div>
    </div>
    
    <!-- 拼图区域 -->
    <div class="puzzle-area">
      <!-- 预览图 -->
      <div class="preview-container">
        <div class="preview-image" :style="{ backgroundImage: `url(${currentBuilding.image})` }"></div>
        <h3>{{ currentBuilding.name }}</h3>
        <p class="building-description">{{ currentBuilding.description }}</p>
        
        <div class="game-stats">
          <div class="stat-item">
            <div class="stat-icon time-icon"></div>
            <div class="stat-value">{{ formatTime(gameTime) }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-icon moves-icon"></div>
            <div class="stat-value">{{ moves }} 步</div>
          </div>
        </div>
        
        <div class="game-controls">
          <button class="control-btn hint-btn" @click="showHint" :disabled="hintsUsed >= 3 || gameCompleted">
            提示 ({{ 3 - hintsUsed }}/3)
          </button>
          <button class="control-btn restart-btn" @click="restartGame">
            重新开始
          </button>
          <button class="control-btn level-btn" @click="showLevelSelector = true">
            选择关卡
          </button>
        </div>
      </div>
      
      <!-- 拼图板 -->
      <div class="puzzle-board" ref="board" @mousemove="handleMove" @touchmove.prevent="handleMove">
        <!-- 拼图底图（灰色轮廓） -->
        <div class="puzzle-outline" :style="{ backgroundImage: `url(${currentBuilding.outlineImage})` }"></div>
        
        <!-- 拼图网格 -->
        <div class="puzzle-grid">
          <div 
            v-for="(cell, index) in gridCells" 
            :key="'cell-'+index" 
            class="grid-cell"
            :class="{ 'correct-position': cell.isOccupied }"
            :style="{ 
              width: `${100 / PUZZLE_SIZE}%`, 
              height: `${100 / PUZZLE_SIZE}%`,
              top: `${Math.floor(index / PUZZLE_SIZE) * (100 / PUZZLE_SIZE)}%`,
              left: `${(index % PUZZLE_SIZE) * (100 / PUZZLE_SIZE)}%`
            }"
          ></div>
        </div>
        
        <!-- 拼图碎片（已放置在拼图板上的） -->
        <div 
          v-for="piece in placedPieces" 
          :key="'placed-'+piece.id"
          class="puzzle-piece correct"
          :style="{ 
            width: `${100 / PUZZLE_SIZE}%`, 
            height: `${100 / PUZZLE_SIZE}%`,
            top: `${piece.correctY}%`,
            left: `${piece.correctX}%`
          }"
        >
          <div 
            class="piece-image"
            :style="getPieceImageStyle(piece)"
          ></div>
          <div class="piece-border"></div>
        </div>
      </div>
    </div>

    <!-- 拼图碎片托盘 -->
    <div class="piece-tray-container">
      <div class="piece-tray" ref="pieceTray">
        <div 
          v-for="piece in unplacedPieces" 
          :key="'unplaced-'+piece.id"
          class="puzzle-piece"
          :class="{ 'selected': selectedPieceId === piece.id, 'hint-piece': hintPieceId === piece.id }"
          :style="{ 
            width: `${trayPieceSize}px`, 
            height: `${trayPieceSize}px`,
            transform: selectedPieceId === piece.id ? 'scale(1.05)' : 'scale(1)'
          }"
          @mousedown="startDrag(piece.id, $event)"
          @touchstart.passive="startDrag(piece.id, $event)"
        >
          <div 
            class="piece-image"
            :style="getPieceImageStyle(piece)"
          ></div>
          <div class="piece-border"></div>
        </div>
      </div>
    </div>

    <!-- 拖动中的碎片 -->
    <div 
      v-if="isDragging && draggedPiece"
      class="puzzle-piece dragging"
      :style="{ 
        width: `${draggedPieceSize}px`, 
        height: `${draggedPieceSize}px`,
        top: `${dragPosition.y - draggedPieceSize/2}px`,
        left: `${dragPosition.x - draggedPieceSize/2}px`
      }"
    >
      <div 
        class="piece-image"
        :style="getPieceImageStyle(draggedPiece)"
      ></div>
      <div class="piece-border"></div>
    </div>

    <!-- 进度显示 -->
    <div class="progress">
      <div class="progress-bar" :style="{ width: progress + '%' }"></div>
      <span class="progress-text">{{ completedCount }}/{{ totalPieces }} 部件归位</span>
    </div>

    <!-- 完成效果 -->
    <transition name="celebrate">
      <div v-if="completed" class="completion-overlay">
        <div class="completion-content">
          <div class="firework"></div>
          <h2 class="complete-title">古建重生！</h2>
          <div class="completion-image" :style="{ backgroundImage: `url(${currentBuilding.image})` }"></div>
          <div class="completion-stats">
            <div class="completion-stat">
              <div class="stat-label">用时</div>
              <div class="stat-value">{{ formatTime(gameTime) }}</div>
            </div>
            <div class="completion-stat">
              <div class="stat-label">步数</div>
              <div class="stat-value">{{ moves }}</div>
            </div>
            <div class="completion-stat">
              <div class="stat-label">得分</div>
              <div class="stat-value">{{ score }}</div>
            </div>
          </div>
          <div class="completion-fact">
            <h4>建筑小知识</h4>
            <p>{{ currentBuilding.fact }}</p>
          </div>
          <div class="completion-buttons">
            <button class="completion-btn next-btn" @click="nextLevel">下一关卡</button>
            <button class="completion-btn restart-btn" @click="restartGame">重新挑战</button>
          </div>
        </div>
      </div>
    </transition>
    
    <!-- 关卡选择器 -->
    <div class="level-selector" v-if="showLevelSelector">
      <div class="level-selector-content">
        <h2>选择关卡</h2>
        <div class="level-grid">
          <div 
            v-for="building in buildings" 
            :key="building.id"
            class="level-item"
            :class="{ 'completed': completedLevels.includes(building.id) }"
            @click="selectLevel(building.id)"
          >
            <div class="level-image" :style="{ backgroundImage: `url(${building.previewImage})` }"></div>
            <div class="level-name">{{ building.name }}</div>
            <div class="level-difficulty">
              <span 
                v-for="n in 3" 
                :key="n" 
                class="difficulty-star"
                :class="{ 'active': n <= building.difficulty }"
              ></span>
            </div>
          </div>
        </div>
        <button class="close-btn" @click="showLevelSelector = false">关闭</button>
      </div>
    </div>
    
    <!-- 游戏开始介绍 -->
    <div class="intro-modal" v-if="showIntro">
      <div class="intro-content">
        <h2>欢迎来到晋韵寻踪·古建拼合</h2>
        <div class="intro-image" :style="{ backgroundImage: `url(${currentBuilding.previewImage})` }"></div>
        <p>拖动拼图碎片，重现山西古建筑的辉煌！</p>
        <ul class="intro-instructions">
          <li><div class="instruction-icon drag-icon"></div> 从下方托盘拖动碎片放置到正确位置</li>
          <li><div class="instruction-icon hint-icon"></div> 使用提示可以高亮一个正确的碎片</li>
          <li><div class="instruction-icon return-icon"></div> 放置错误的碎片会自动返回托盘</li>
        </ul>
        <button class="start-btn" @click="startGame">开始游戏</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, reactive,watch } from 'vue';

// 游戏配置
const PUZZLE_SIZE = 3; // 3x3拼图
// const SNAP_THRESHOLD = 15; // 吸附阈值（像素）
const totalPieces = PUZZLE_SIZE * PUZZLE_SIZE;

// 主题模式
const isNightMode = ref(false);

// 游戏状态
const gameStarted = ref(false);
const gameCompleted = ref(false);
const gameTime = ref(0);
const timerInterval = ref(null);
const moves = ref(0);
const score = ref(0);
const hintsUsed = ref(0);
const hintPieceId = ref(null);
const showingHint = ref(false);

// 拼图设置
const pieces = ref([]);
const gridCells = ref([]);
const selectedPieceId = ref(null);
const isDragging = ref(false);
const board = ref(null);
const pieceTray = ref(null);
const dragPosition = reactive({ x: 0, y: 0 });
const draggedPieceSize = ref(100); // 默认值，会在运行时更新
const trayPieceSize = ref(60); // 托盘中碎片的大小

// 界面控制
const showIntro = ref(true);
const showLevelSelector = ref(false);
const completedLevels = ref([]);

// 当前建筑
const currentBuildingId = ref('wooden-pagoda');

// 建筑数据
const buildings = [
  {
    id: 'wooden-pagoda',
    name: '应县木塔',
    difficulty: 1,
    image: '/images/应县木塔.png',
    outlineImage: '',
    previewImage: '/images/应县木塔.png',
    description: '应县木塔，又称佛宫寺释迦塔，位于山西省朔州市应县佛宫寺内，始建于辽清宁二年（公元1056年），是中国现存最高最古老的木塔。',
    fact: '应县木塔高67.31米，是世界上现存最高最古老的木塔，采用榫卯结构，无一钉一铆，历经近千年风雨依然屹立不倒。'
  },
  {
    id: 'hanging-temple',
    name: '悬空寺',
    difficulty: 2,
    image: '/images/悬空寺.png',
    outlineImage: '',
    previewImage: '/images/悬空寺.png',
    description: '悬空寺始建于北魏后期（约公元491年），位于山西省大同市浑源县恒山金龙峡西侧翠屏峰的峭壁间，是中国仅存的佛、道、儒三教合一的独特寺庙。',
    fact: '悬空寺距地面约50米，建在悬崖峭壁上，整个建筑群靠着几十根看似摇摇欲坠的木柱支撑，被誉为"世界十大奇险建筑"之一。'
  },
  {
    id: 'pingyao',
    name: '平遥古城',
    difficulty: 3,
    image: '/images/pingyao.png',
    outlineImage: '',
    previewImage: '/images/pingyao.png',
    description: '平遥古城位于山西省中部，始建于西周宣王时期（公元前827年-公元前782年），是中国保存最为完整的四大古城之一，1997年被列入世界文化遗产名录。',
    fact: '平遥古城是中国汉民族城市在明清时期的杰出范例，其城墙全长6.4公里，有6座城门，72座敌楼，是中国仅存的四座完整古城墙之一。'
  }
];


// 计算属性
const currentBuilding = computed(() => {
  return buildings.find(b => b.id === currentBuildingId.value) || buildings[0];
});

const placedPieces = computed(() => {
  return pieces.value.filter(p => p.isPlaced) || [];}
);

const unplacedPieces = computed(() => {
  return pieces.value.filter(p => !p.isPlaced) || [];}
);

const completedCount = computed(() => 
  placedPieces.value.length
);

const progress = computed(() => 
  (completedCount.value / totalPieces) * 100
);

const completed = computed(() => 
  completedCount.value === totalPieces
);

const draggedPiece = computed(() => {
    if (!isDragging.value || selectedPieceId.value === null) {
        return null;
    }
    const piece = pieces.value.find(p => p.id === selectedPieceId.value);
    return piece || null;
});

// 初始化拼图
const initializePuzzle = () => {
  // 创建网格单元格
  const cells = [];
  for (let i = 0; i < PUZZLE_SIZE; i++) {
    for (let j = 0; j < PUZZLE_SIZE; j++) {
      cells.push({
        row: i,
        col: j,
        isOccupied: false
      });
    }
  }
  gridCells.value = cells;
  
  // 创建拼图碎片
  const newPieces = [];
  for (let i = 0; i < PUZZLE_SIZE; i++) {
    for (let j = 0; j < PUZZLE_SIZE; j++) {
      newPieces.push({
        id: i * PUZZLE_SIZE + j,
        correctRow: i,
        correctCol: j,
        correctX: j * 100 / (PUZZLE_SIZE ),
        correctY: i * 100 / (PUZZLE_SIZE ),
        isPlaced: false,
        imageUrl: generatePieceImageUrl(i, j)
      });
      console.log('生成的拼图碎片:', newPieces); // 调试输出
    }
  }

  // 打乱顺序
  newPieces.sort(() => Math.random() - 0.5);
  pieces.value = newPieces;
  // 更新拖动碎片的大小
  updateDraggedPieceSize();

};

watch(currentBuildingId, () => {
  initializePuzzle();
});

// 生成碎片图片URL
const generatePieceImageUrl = (row, col) => {  
  try {
        // 构建图片文件名
        const fileName = `${currentBuilding.value.id}/piece_${row}_${col}.png`;
        // 构建完整的图片路径
        const imageUrl = `/images/puzzle/${fileName}`;
        // 返回图片URL
         console.log('生成的图片URL:', imageUrl); // 调试输出
        return imageUrl;
    } catch (error) {
        console.error('生成图片URL时出错:', error);
        return null;
    }
};

// 获取碎片图片样式
const getPieceImageStyle = (piece) => {

  if (!piece) {
        return {};
    }
  
  const text = piece.imageUrl;
  console.log('拼图碎片的图片URL:', text)
   // 调试输出
  return {
    backgroundImage: `url(${piece.imageUrl})`,
    backgroundSize: `100% 100%`,
    backgroundPosition: `${-piece.correctCol * (100 / (PUZZLE_SIZE - 1))}% ${
  -piece.correctRow * (100 / (PUZZLE_SIZE - 1))
}%`
  };
};

// 更新拖动碎片的大小
const updateDraggedPieceSize = () => {
  if (!board.value) return;
  
  const boardRect = board.value.getBoundingClientRect();
  draggedPieceSize.value = Math.min(boardRect.width, boardRect.height) / PUZZLE_SIZE;
  
  // 托盘中碎片的大小稍小一些
  trayPieceSize.value = draggedPieceSize.value * 0.8;
};

// 拖拽逻辑
const startDrag = (pieceId, event) => {
  const piece = pieces.value.find(p => p.id === pieceId);
  if (!piece || piece.isPlaced) return;
  
  selectedPieceId.value = pieceId;
  isDragging.value = true;
  moves.value++;
  
  // 设置初始拖动位置
  const { clientX, clientY } = event.touches?.[0] || event;
  dragPosition.x = clientX;
  dragPosition.y = clientY;
  
  window.addEventListener('mousemove', handleMove);
  window.addEventListener('touchmove', handleMove, { passive: false });
  window.addEventListener('mouseup', stopDrag);
  window.addEventListener('touchend', stopDrag);
  
  // 阻止默认行为以防止滚动
  event.preventDefault();
};

const handleMove = (event) => {
  if (!isDragging.value) return;
  
  const { clientX, clientY } = event.touches?.[0] || event;
  dragPosition.x = clientX;
  dragPosition.y = clientY;
  
  // 阻止默认行为以防止滚动
  event.preventDefault();
};

const stopDrag = () => {
  if (!isDragging.value) return;
  
  window.removeEventListener('mousemove', handleMove);
  window.removeEventListener('touchmove', handleMove);
  window.removeEventListener('mouseup', stopDrag);
  window.removeEventListener('touchend', stopDrag);
  
  // 检查是否放置在正确位置
  checkPlacement();
  
  isDragging.value = false;
  selectedPieceId.value = null;
};

// 检查碎片放置
const checkPlacement = () => {
  if (!board.value || !draggedPiece.value) return;

const boardRect = board.value.getBoundingClientRect();
const pieceSize = boardRect.width / PUZZLE_SIZE; // 单个网格的宽度（正方形）

// 计算拖动位置在拼图板内的相对坐标（修正边界情况）
const relativeX = Math.max(0, Math.min(dragPosition.x - boardRect.left, boardRect.width));
const relativeY = Math.max(0, Math.min(dragPosition.y - boardRect.top, boardRect.height));

// 计算吸附到最近的网格中心（提高容错率）
const col = Math.round(relativeX / pieceSize);
const row = Math.round(relativeY / pieceSize);

// 确保坐标在有效范围内
if (row < 0 || row >= PUZZLE_SIZE || col < 0 || col >= PUZZLE_SIZE) {
  return;
}

const cellIndex = row * PUZZLE_SIZE + col;
const cell = gridCells.value[cellIndex];

// 检查单元格是否未被占用且位置正确
if (!cell.isOccupied && 
    draggedPiece.value.correctRow === row && 
    draggedPiece.value.correctCol === col) {
  // 放置碎片
  draggedPiece.value.isPlaced = true;
  cell.isOccupied = true;
  playSnapSound();
    
    // 更新分数
    score.value += 100 + Math.max(0, 50 - moves.value);
    
    // 检查是否完成
    if (completed.value) {
      gameCompleted.value = true;
      stopTimer();
      completedLevels.value.push(currentBuildingId.value);
      localStorage.setItem('completedLevels', JSON.stringify(completedLevels.value));
    }
  } else {
    // 错误位置，播放错误音效
    playErrorSound();
  }
};

// 提示功能
const showHint = () => {
  if (hintsUsed.value >= 3 || gameCompleted.value) return;
  
  hintsUsed.value++;
  
  // 找到一个未放置的碎片
  const unplaced = unplacedPieces.value;
  if (unplaced.length === 0) return;
  
  // 随机选择一个未放置的碎片
  const randomPiece = unplaced[Math.floor(Math.random() * unplaced.length)];
  hintPieceId.value = randomPiece.id;
  
  // 闪烁效果
  showingHint.value = true;
  setTimeout(() => {
    showingHint.value = false;
    hintPieceId.value = null;
  }, 3000);
};

// 游戏控制
const startGame = () => {
  showIntro.value = false;
  gameStarted.value = true;
  restartGame();
  startTimer();
};

const restartGame = () => {
  stopTimer();
  gameTime.value = 0;
  moves.value = 0;
  score.value = 0;
  hintsUsed.value = 0;
  gameCompleted.value = false;
  initializePuzzle();
  startTimer();
};

const selectLevel = (levelId) => {
  if (currentBuildingId.value !== levelId) {
    currentBuildingId.value = levelId;
    restartGame();
  }
  showLevelSelector.value = false;
};

const nextLevel = () => {
  const currentIndex = buildings.findIndex(b => b.id === currentBuildingId.value);
  const nextIndex = (currentIndex + 1) % buildings.length;
  currentBuildingId.value = buildings[nextIndex].id;
  restartGame();
};

// 计时器
const startTimer = () => {
  if (timerInterval.value) clearInterval(timerInterval.value);
  timerInterval.value = setInterval(() => {
    gameTime.value++;
  }, 1000);
};

const stopTimer = () => {
  if (timerInterval.value) {
    clearInterval(timerInterval.value);
    timerInterval.value = null;
  }
};

// 工具函数
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// 音效处理
const playSnapSound = () => {
  try {
    const audio = new Audio('/audio/snap.mp3');
    audio.volume = 0.3;
    audio.play().catch(e => console.log('Audio play failed:', e));
  } catch (e) {
    console.log('Audio error:', e);
  }
};

const playErrorSound = () => {
  try {
    const audio = new Audio('/audio/error.mp3');
    audio.volume = 0.2;
    audio.play().catch(e => console.log('Audio play failed:', e));
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// 主题切换
const toggleNightMode = () => {
  isNightMode.value = !isNightMode.value;
  localStorage.setItem('nightMode', isNightMode.value ? 'true' : 'false');
};

// 窗口大小变化时更新拖动碎片的大小
const handleResize = () => {
  updateDraggedPieceSize();
};

// 生命周期钩子
onMounted(() => {
  // 加载保存的主题
  const savedNightMode = localStorage.getItem('nightMode');
  if (savedNightMode) {
    isNightMode.value = savedNightMode === 'true';
  }
  
  // 加载已完成关卡
  const savedCompletedLevels = localStorage.getItem('completedLevels');
  if (savedCompletedLevels) {
    try {
      completedLevels.value = JSON.parse(savedCompletedLevels);
    } catch (e) {
      console.error('Failed to parse completed levels:', e);
    }
  }
  
  initializePuzzle();
  console.log('pieces:', pieces.value); // 检查 pieces 数组是否正确初始化
  
  // 添加窗口大小变化监听
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  stopTimer();
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('mousemove', handleMove);
  window.removeEventListener('touchmove', handleMove);
  window.removeEventListener('mouseup', stopDrag);
  window.removeEventListener('touchend', stopDrag);
});
</script>

<style scoped>
.game-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Microsoft YaHei', sans-serif;
  color: #2c3e50;
  background-color: #f9f6f0;
  transition: background-color 0.3s, color 0.3s;
}

.game-container.night-mode {
  background-color: #1a1a2e;
  color: #e0e0e0;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

header::before {
  background: none !important; /* 或者新的背景值 */
}

.title {
  font-size: 2.5rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.1);
  background: linear-gradient(45deg, #8b4513, #c87137);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.night-mode .title {
  background: linear-gradient(45deg, #f0c78a, #e6a23c);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.mode-toggle {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background: #f0e6d3;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: background 0.3s;
}

.night-mode .mode-toggle {
  background: #2d3748;
}

.toggle-icon {
  width: 24px;
  height: 24px;
  margin-right: 8px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23f39c12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>') no-repeat center;
  transition: transform 0.3s;
}

.toggle-icon.night {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23f1c40f" stroke-width="2" stroke-linecap  viewBox="0 0 24 24" fill="none" stroke="%23f1c40f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>') no-repeat center;
  transform: rotate(360deg);
}

.puzzle-area {
  width: 100%;
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
}

@media (max-width: 768px) {
  .puzzle-area {
    flex-direction: column;
  }
}

.preview-container {
  flex: 1;
  max-width: 300px;
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: background 0.3s, box-shadow 0.3s;
}

.night-mode .preview-container {
  background: #2d3748;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.preview-image {
  width: 100%;
  height: 180px;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  /* margin-bottom: 1rem; */
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.building-description {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #666;
  margin-bottom: 1.5rem;
}

.night-mode .building-description {
  color: #a0aec0;
}

.game-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.stat-item {
  display: flex;
  align-items: center;
}

.stat-icon {
  width: 24px;
  height: 24px;
  margin-right: 8px;
}

.time-icon {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%238b4513" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>') no-repeat center;
}

.night-mode .time-icon {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23e6a23c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>') no-repeat center;
}

.moves-icon {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%238b4513" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9l4-4 4 4"/><path d="M5 15l4 4 4-4"/><path d="M19 5l-4 4-4-4"/><path d="M19 19l-4-4-4 4"/></svg>') no-repeat center;
}

.night-mode .moves-icon {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23e6a23c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 9l4-4 4 4"/><path d="M5 15l4 4 4-4"/><path d="M19 5l-4 4-4-4"/><path d="M19 19l-4-4-4 4"/></svg>') no-repeat center;
}

.game-controls {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.control-btn {
  padding: 0.8rem 1rem;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hint-btn {
  background: #4CAF50;
  color: white;
}

.hint-btn:disabled {
  background: #a5d6a7;
  cursor: not-allowed;
}

.restart-btn {
  background: #ff9800;
  color: white;
}

.level-btn {
  background: #2196F3;
  color: white;
}

.control-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

.puzzle-board {
  flex: 2;
  position: relative;
  width: 100%;
  height: 580px;
  background: #f0e6d3;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: background 0.3s, box-shadow 0.3s;
}

.night-mode .puzzle-board {
  background: #2d3748;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.puzzle-outline {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.2;
  pointer-events: none;
}

.puzzle-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.grid-cell {
  position: absolute;
  border: 1px dashed rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
}

.night-mode .grid-cell {
  border-color: rgba(255, 255, 255, 0.1);
}

.grid-cell.correct-position {
  border-color: rgba(76, 175, 80, 0.3);
  background-color: rgba(76, 175, 80, 0.05);
}

.puzzle-piece {
  position: static;
  flex: 0 0 120px; /* 固定尺寸，不拉伸不缩小 */
  margin: 0 8px; /* 左右间距，使碎片不紧贴 */
  background-size: cover; /* 保持图片原始比例，避免拉伸 */
  background-position: center; /* 图片居中显示 */
  display: inline-flex; /* 确保块级元素但不脱离文档流 */
  cursor: grab;
  border: 2px solid rgba(255, 255, 255, 0.8);
  background: #fff;
  /* box-sizing: border-box; */
  transition: transform 0.2s, box-shadow 0.2s;
  /* overflow: hidden; */
  border-radius: 4px;
}

.piece-tray {
  display: flex;
  white-space: nowrap; /* 禁止换行 */
  align-items: center; /* 垂直居中 */
  padding: 0.5rem;
  overflow-x: auto; /* 水平滚动条（碎片过多时） */
  overflow-y: hidden; /* 禁止垂直滚动 */
  display: flex;
  gap: 10px; /* 增加碎片间水平间距，避免重合 */
  padding: 10px; /* 容器内边距，避免边缘紧贴 */
}

.night-mode .puzzle-piece {
  border-color: rgba(255, 255, 255, 0.3);
  background: #4a5568;
}

.puzzle-piece.selected {
  cursor: grabbing;
  z-index: 10;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
  transform: scale(1.05); /* 选中时放大 */
}

.puzzle-piece.correct {
  position: absolute;
  cursor: default;
  border-color: #4CAF50;
  box-shadow: 0 0 15px rgba(76, 175, 80, 0.4);
}

.puzzle-piece.hint-piece {
  animation: pulse 1s infinite alternate;
  border-color: #ff9800;
  box-shadow: 0 0 15px rgba(255, 152, 0, 0.6);
}

.puzzle-piece.dragging {
 width: 100px;
 height: 100px;
  position: fixed;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 8px 24px rgba(0,0,0,0.3);
}

.piece-image {
  background-size: cover; /* 重要：保持图片原始比例，填充容器 */
  background-position: center; /* 图片居中显示 */
  width: 100%;
  height: 100%;
  background-size: 300% 300%;
  background-repeat: no-repeat;
  transition: filter 0.3s;
}

.night-mode .piece-image {
  filter: brightness(0.85);
}

.piece-border {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  border: 2px solid rgba(255, 255, 255, 0.5);
  box-sizing: border-box;
}

.piece-tray-container {
  width: 100%;
  margin: 1rem 0;
  background: #e6dcc6;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.1);
  transition: background 0.3s;
  min-height: 125px; /* 确保高度大于碎片高度 */
}

.night-mode .piece-tray-container {
  background: #1e293b;
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.3);
}

.piece-tray {
  display: flex;
  /* flex-wrap: wrap; */
  gap: 1rem;
  justify-content: center;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
  flex-wrap: nowrap; /* 禁止换行，水平排列 */
  justify-content: flex-start; /* 左对齐 */
  align-items: center; /* 垂直居中 */
  padding: 0.5rem;
  overflow-x: auto; /* 水平滚动条（碎片过多时） */
  overflow-y: hidden; /* 禁止垂直滚动 */
}

.progress {
  height: 24px;
  background: #eee;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.1);
}

.night-mode .progress {
  background: #4a5568;
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.3);
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  transition: width 0.5s ease;
}

.progress-text {
  display: block;
  text-align: center;
  margin-top: 0.5rem;
  color: #666;
}

.night-mode .progress-text {
  color: #a0aec0;
}

.completion-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.completion-content {
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  animation: slideUp 0.5s ease-out;
}

.night-mode .completion-content {
  background: #2d3748;
  color: #e2e8f0;
}

.completion-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.completion-icon {
  width: 40px;
  height: 40px;
  margin-right: 12px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%234CAF50" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>') no-repeat center;
}

.completion-image {
  width: 100%;
  height: 180px;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin: 1rem 0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.completion-stats {
  display: flex;
  justify-content: space-around;
  margin: 1.5rem 0;
}

.completion-stat {
  text-align: center;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.3rem;
}

.night-mode .stat-label {
  color: #a0aec0;
}

.completion-fact {
  background: #f5f5f5;
  padding: 1rem;
  border-radius: 8px;
  margin: 1.5rem 0;
  text-align: left;
}

.night-mode .completion-fact {
  background: #4a5568;
}

.completion-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.completion-btn {
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.next-btn {
  background: #4CAF50;
  color: white;
}

.restart-btn {
  background: #ff9800;
  color: white;
}

.completion-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

.level-selector {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.level-selector-content {
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  max-width: 800px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  animation: slideUp 0.5s ease-out;
}

.night-mode .level-selector-content {
  background: #2d3748;
  color: #e2e8f0;
}

.level-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
}

.level-item {
  background: #f5f5f5;
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.night-mode .level-item {
  background: #4a5568;
}

.level-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.15);
}

.level-item.completed {
  border: 2px solid #4CAF50;
}

.level-image {
  width: 100%;
  height: 120px;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.level-name {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.level-difficulty {
  display: flex;
  gap: 4px;
}

.difficulty-star {
  width: 16px;
  height: 16px;
  background: #ddd;
  clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
}

.difficulty-star.active {
  background: #FFD700;
}

.close-btn {
  display: block;
  margin: 0 auto;
  padding: 0.8rem 2rem;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.close-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

.intro-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.intro-content {
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  animation: slideUp 0.5s ease-out;
}

.night-mode .intro-content {
  background: #2d3748;
  color: #e2e8f0;
}

.intro-image {
  width: 100%;
  height: 180px;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin: 1rem 0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.intro-instructions {
  text-align: left;
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
}

.intro-instructions li {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.instruction-icon {
  width: 24px;
  height: 24px;
  margin-right: 12px;
}

.drag-icon {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%238b4513" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>') no-repeat center;
}

.night-mode .drag-icon {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23e6a23c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>') no-repeat center;
}

.hint-icon {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%238b4513" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>') no-repeat center;
}

.night-mode .hint-icon {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23e6a23c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>') no-repeat center;
}

.return-icon {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%238b4513" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 10l-5 5 5 5"/><path d="M20 4v7a4 4 0 0 1-4 4H4"/></svg>') no-repeat center;
}

.night-mode .return-icon {
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23e6a23c" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 10l-5 5 5 5"/><path d="M20 4v7a4 4 0 0 1-4 4H4"/></svg>') no-repeat center;
}

.start-btn {
  padding: 1rem 2rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
}

.start-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0,0,0,0.2);
}

.firework {
  width: 100px;
  height: 100px;
  background: radial-gradient(circle, #ffeb3b 10%, transparent 20%),
    repeating-conic-gradient(from 0deg, #ff5722 0deg 15deg, #ffeb3b 15deg 30deg);
  animation: explode 1s ease-out;
  border-radius: 50%;
  margin: 0 auto 1rem;
}

.complete-title {
  color: #4CAF50;
  font-size: 2.5em;
  margin-bottom: 1rem;
  animation: float 2s ease-in-out infinite;
}

@keyframes explode {
  0% { transform: scale(0); opacity: 1; }
  100% { transform: scale(3); opacity: 0; }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes pulse {
  0% { transform: scale(1); }
  100% { transform: scale(1.05); }
}

@keyframes slideUp {
  0% { transform: translateY(50px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}

.celebrate-enter-active,
.celebrate-leave-active {
  transition: opacity 0.5s;
}
.celebrate-enter-from,
.celebrate-leave-to {
  opacity: 0;
}
</style>